'use client';

/**
 * RagAnswerBlock — the composed assistant message for a RAG turn.
 *
 * Design 4a ("text-outside-the-bubble / video-below"): the answer streams
 * FIRST as bare prose on the canvas, then the media follows underneath —
 *   - exactly one reference  → MediaHeroCard (full-width featured card,
 *     title + category chip over the thumbnail);
 *   - multiple references    → RelatedMediaPanel (master-detail list +
 *     featured pane; mixed videos/articles ride the same panel).
 *
 * The media gates on `answerDone` so the panel doesn't pop in and shove the
 * streaming text around; past turns are always done, so restored history
 * shows text + media immediately. The old top-hero + "SOURCES" mini-tile
 * rail is fully superseded by these two components.
 */

import React, { memo, useMemo } from 'react';
import { MessageResponse } from '@/components/ai-elements/message';
import type { RAGReference } from '@/types/rag';
import { cn } from '@/lib/utils';
import { ASSISTANT_PROSE } from '@/lib/design-tokens';
import { FadeSlideIn } from '../fade-slide-in';
import { pickMediaComponent } from './pick-media';

interface RagAnswerBlockProps {
  refs: RAGReference[];
  answer: string | null;
  answerDone: boolean;
  /** False for restored turns — reloaded transcripts render statically,
   *  never replaying the live entrance (same invariant as MessageBlock's
   *  `restored` / ContactSummaryCard.animateIn). Defaults to true (live). */
  animateMediaIn?: boolean;
  className?: string;
}

export const RagAnswerBlock = memo(function RagAnswerBlock({
  refs,
  answer,
  answerDone,
  animateMediaIn = true,
  className,
}: RagAnswerBlockProps) {
  const sorted = useMemo(
    () => [...refs].sort((a, b) => (b.score ?? 0) - (a.score ?? 0)),
    [refs]
  );

  return (
    <div className={cn('flex w-full flex-col gap-[16px]', className)}>
      {/* Answer — bare prose on the canvas, no bubble (design 4a). Shared
          ASSISTANT_PROSE typography with message-block's assistant branch
          (FB21: every assistant prose path renders bare, one voice). */}
      {answer && (
        <div className={ASSISTANT_PROSE}>
          <MessageResponse>{answer}</MessageResponse>
        </div>
      )}

      {/* Media below the answer, once the answer has settled. FadeSlideIn
          gives the card the same "content arriving" entrance as every other
          tool card (MessageBlock's ToolResultSlot — this composed flow
          bypasses MessageBlock, so it applies the same wrapper itself);
          suppressed for restored turns via animateMediaIn. */}
      {answerDone &&
        sorted.length > 0 &&
        (() => {
          const media = pickMediaComponent(sorted);
          return animateMediaIn ? <FadeSlideIn>{media}</FadeSlideIn> : media;
        })()}
    </div>
  );
});

export default RagAnswerBlock;
